import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import {
  acknowledgeEmployeeReview,
  getEmployeeReviewSubmission,
  getEmployeeReviewsOverview,
  getEmployeeSharedReview,
  nominateReviewPeers,
  saveEmployeeReviewSubmission,
  searchPerformanceColleagues,
} from '@/api/performance';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';
import type {
  NominatePeersVariables,
  SaveReviewVariables,
} from '@/types/performance';
import { usePerformanceEnabled } from './usePerformanceSummary';

/*
 * Errors are not toasted here: the API client's response interceptor already
 * shows the backend's (translated) message for every failed request, and a
 * second toast from `onError` would only replace it with vaguer copy.
 */

export function useEmployeeReviewsOverview() {
  const { user } = useEmployeePortalAuth();
  // Read on every dashboard: never while the module is off (PF-21).
  const on = usePerformanceEnabled();

  return useQuery({
    queryKey: [QueryKeys.EmployeeReviewsOverview],
    queryFn: getEmployeeReviewsOverview,
    enabled: !!user && on,
  });
}

export function useEmployeeReviewSubmission(submissionId: string | undefined) {
  const { user } = useEmployeePortalAuth();

  return useQuery({
    queryKey: [QueryKeys.EmployeeReviewSubmission, submissionId],
    queryFn: () => getEmployeeReviewSubmission(submissionId!),
    enabled: !!user && !!submissionId,
  });
}

export function useSaveReviewSubmission(submissionId: string | undefined) {
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.Performance);

  return useMutation({
    mutationFn: ({ answers, submit }: SaveReviewVariables) =>
      saveEmployeeReviewSubmission(submissionId!, { answers, submit }),
    onSuccess: (response, { submit, quiet }) => {
      qc.setQueryData(
        [QueryKeys.EmployeeReviewSubmission, submissionId],
        response
      );
      qc.invalidateQueries({ queryKey: [QueryKeys.EmployeeReviewsOverview] });
      if (quiet) return;
      toast.success(t(submit ? 'toasts.reviewSubmitted' : 'toasts.draftSaved'));
    },
  });
}

export function useEmployeeSharedReview(reviewId: string | undefined) {
  const { user } = useEmployeePortalAuth();

  return useQuery({
    queryKey: [QueryKeys.EmployeeSharedReview, reviewId],
    queryFn: () => getEmployeeSharedReview(reviewId!),
    enabled: !!user && !!reviewId,
  });
}

export function useAcknowledgeReview(reviewId: string | undefined) {
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.Performance);

  return useMutation({
    mutationFn: (comment?: string) =>
      acknowledgeEmployeeReview(reviewId!, comment ? { comment } : {}),
    onSuccess: (response) => {
      qc.setQueryData([QueryKeys.EmployeeSharedReview, reviewId], response);
      qc.invalidateQueries({ queryKey: [QueryKeys.EmployeeReviewsOverview] });
      toast.success(t('toasts.acknowledged'));
    },
  });
}

export function usePerformanceColleagues(search: string, enabled = true) {
  const { user } = useEmployeePortalAuth();

  return useQuery({
    queryKey: [QueryKeys.PerformanceColleagues, search],
    queryFn: () => searchPerformanceColleagues(search),
    enabled: !!user && enabled,
    staleTime: 60_000,
  });
}

export function useNominatePeers() {
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.Performance);

  return useMutation({
    mutationFn: ({ reviewId, peerEmployeeIds }: NominatePeersVariables) =>
      nominateReviewPeers(reviewId, { peerEmployeeIds }),
    onSuccess: (response) => {
      qc.setQueryData([QueryKeys.EmployeeReviewsOverview], response);
      toast.success(t('toasts.peersSaved'));
    },
  });
}
